import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LogConsole = ({ logs, onClear, onExport }) => {
  const [filterLevel, setFilterLevel] = useState('all');

  const filteredLogs = logs?.filter(log => {
    if (filterLevel === 'all') return true;
    return log?.level === filterLevel;
  });

  const getLevelConfig = (level) => {
    const configs = {
      error: { color: 'text-red-500', bgColor: 'bg-red-500/10', icon: 'XCircle' },
      warn: { color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', icon: 'AlertTriangle' },
      info: { color: 'text-blue-500', bgColor: 'bg-blue-500/10', icon: 'Info' },
      success: { color: 'text-green-500', bgColor: 'bg-green-500/10', icon: 'CheckCircle2' }
    };
    return configs?.[level] || configs?.info;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Terminal" size={20} />
          Live Logs
          <span className="text-sm font-normal text-muted-foreground">({filteredLogs?.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          {/* Filter Buttons */}
          <div className="flex items-center gap-1 mr-2">
            {['all', 'error', 'warn', 'info', 'success']?.map((level) => (
              <button
                key={level}
                onClick={() => setFilterLevel(level)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  filterLevel === level
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={logs?.length === 0}
            className="flex items-center gap-2"
          >
            <Icon name="Download" size={16} />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onClear}
            disabled={logs?.length === 0}
            className="flex items-center gap-2"
          >
            <Icon name="Trash2" size={16} />
            Clear
          </Button>
        </div>
      </div>
      {/* Log Console */}
      <div className="bg-background rounded-lg border border-border p-4 max-h-96 overflow-y-auto font-mono text-sm">
        {filteredLogs?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Terminal" size={32} className="mx-auto mb-2 opacity-50" />
            <p>No logs to display</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredLogs?.map((log) => {
              const config = getLevelConfig(log?.level);
              return (
                <div
                  key={log?.id}
                  className={`p-3 rounded-lg ${config?.bgColor} border border-border`}
                >
                  <div className="flex items-start gap-3">
                    <Icon name={config?.icon} size={16} className={`${config?.color} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold uppercase ${config?.color}`}>
                          {log?.level}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.timestamp)?.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-foreground mb-1">{log?.message}</div>
                      {log?.data && Object.keys(log?.data)?.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            View details
                          </summary>
                          <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto">
                            {JSON.stringify(log?.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LogConsole;